<template>
  <v-card outlined>
    <v-container class="px-1 pt-1 pb-4" fluid>
      <v-responsive max-width="1200px">
        <v-card-title class="py-2">
          {{ $t('ELE0028') }}
        </v-card-title>
        <v-card-text>
          <v-form @submit.prevent="onClickSearch">
            <v-row align-content="space-between">
              <v-col cols="12" md="6">
                <TextFieldRef v-if="!isNone" :label="$t('ELE0136')" :value="searchCondition.returnNo" />
                <TextFieldInput v-else :label="$t('ELE0136')" v-model="searchCondition.returnNo" />
              </v-col>
              <v-col cols="12" md="6" v-if="!isInquiry && !isEditOnly">
                <v-row justify="end">
                  <v-card-title>
                    <BtnSearch @click="onClickSearch" :disabled="!isNone || !searchCondition.returnNo" />
                  </v-card-title>
                  <v-card-title>
                    <BtnNew @click="onClickNew" :disabled="!isNone" />
                  </v-card-title>
                  <v-card-title>
                    <BtnReset @click="onClickReset" :disabled="isNone" />
                  </v-card-title>
                </v-row>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <validation-observer v-if="!isNone" ref="observer" v-slot="{ invalid }">
          <v-card outlined>
            <v-card-title class="py-2">
              {{ inputLabel }}
            </v-card-title>
            <v-card-text>
              <v-form :disabled="isInquiry">
                <v-row>
                  <v-col>
                    <validation-provider v-slot="{ errors }" :name="$t('ELE0031')" rules="required">
                      <PartCodeInput
                        v-if="isEpson"
                        :label="$t('ELE0031')"
                        value="value"
                        :error-messages="errors"
                        @input="onPartSelectOrLotChange"
                        v-model="inputItems.partCd"
                        required
                      />
                      <SupplierPartInput
                        v-else
                        :label="$t('ELE0031')"
                        :supplierCd="''"
                        :error-messages="errors"
                        @change="onPartSelectOrLotChange"
                        v-model="inputItems.partCd"
                        required
                        isSearchByLoginCompany
                      />
                    </validation-provider>
                  </v-col>
                </v-row>
                <v-row class="mt-1">
                  <v-col cols="6">
                    <TextFieldInput
                      :label="$t('ELE0049')"
                      v-model="inputItems.lotNo"
                      @change="onPartSelectOrLotChange"
                    />
                  </v-col>
                  <v-col cols="6">
                    <TextFieldRef :label="$t('ELE0119')" :value="acceptanceQty" right />
                  </v-col>
                </v-row>
                <v-row class="mt-1">
                  <v-col cols="6">
                    <validation-provider v-slot="{ errors }" :name="$t('ELE0124')" rules="required">
                      <CompanyInput
                        v-if="isEpson"
                        :label="$t('ELE0124')"
                        value="value"
                        :error-messages="errors"
                        v-model="inputItems.returnCompanyCd"
                        @input="onPartSelectOrLotChange"
                        required
                      />
                      <ManufacturerInput
                        v-else
                        :label="$t('ELE0124')"
                        :error-messages="errors"
                        v-model="inputItems.returnCompanyCd"
                        @input="onPartSelectOrLotChange"
                        required
                      />
                    </validation-provider>
                  </v-col>
                  <v-col cols="6">
                    <TextFieldRef :label="$t('ELE0126')" :value="totalReturnedQty" right />
                  </v-col>
                </v-row>
                <v-row class="mt-1">
                  <v-col cols="6">
                    <validation-provider v-slot="{ errors }" :name="$t('ELE0135')" rules="required">
                      <TextFieldInputWithDatePicker
                        :label="$t('ELE0135')"
                        v-model="inputItems.returnDate"
                        :error-messages="errors"
                        @input="onDateChanged"
                        disableFutureDate
                        required
                      />
                    </validation-provider>
                  </v-col>
                </v-row>
                <v-row class="mt-1">
                  <v-col cols="6">
                    <validation-provider v-slot="{ errors }" :name="$t('ELE0120')" rules="required">
                      <TextFieldInput
                        :label="$t('ELE0120')"
                        :error-messages="errors"
                        rules="maxValue"
                        v-model="inputItems.returnQty"
                        type="number"
                        required
                      />
                    </validation-provider>
                  </v-col>
                  <v-col cols="6">
                    <TextFieldRef :label="$t('ELE0121')" :value="getReturnRate" right />
                  </v-col>
                </v-row>
                <v-row class="mt-1">
                  <v-col>
                    <TextAreaInput :label="$t('ELE0054')" v-model="inputItems.remarks" />
                  </v-col>
                </v-row>
                <v-row class="mt-1" v-if="!isInquiry">
                  <v-col justify="end">
                    <v-row justify="end">
                      <v-card-title>
                        <BtnDelete @click="confirm" :disabled="isAdd" />
                      </v-card-title>
                      <v-card-title>
                        <BtnSave @click="onClickSave" :disabled="invalid" />
                      </v-card-title>
                    </v-row>
                  </v-col>
                </v-row>
              </v-form>
            </v-card-text>
          </v-card>
        </validation-observer>
      </v-responsive>
    </v-container>
    <ConfirmDialog
      :dialog.sync="confirmDialog.flag"
      :confirmWord="confirmDialog.confirmWord"
      :btnLabel="confirmDialog.btnLabel"
      @cancel="cancel"
      @proceed="proceed"
    />
  </v-card>
</template>

<script>
import { API } from '@/plugins/api'
import APIUtils from '@/mixin/api-utils.js'
import Message from '@/mixin/message-utils.js'
import Loading from '@/mixin/loading-utils.js'
import DateUtils from '@/mixin/date-utils.js'
import TextFieldInput from '@/components/atoms/base/text-field/TextFieldInput.vue'
import Btn from '@/components/atoms/base/button/Btn.vue'
import BtnSearch from '@/components/atoms/button/BtnSearch.vue'
import BtnNew from '@/components/atoms/button/BtnNew.vue'
import BtnSave from '@/components/atoms/button/BtnSave.vue'
import TextFieldInputWithDatePicker from '@/components/atoms/base/text-field/TextFieldInputWithDatePicker.vue'
import TextAreaInput from '@/components/atoms/base/text-field/TextAreaInput.vue'
import BtnDelete from '@/components/atoms/button/BtnDelete.vue'
import ConfirmDialog from '@/components/organisms/common/dialog/ConfirmDialog'
import { ValidationObserver, ValidationProvider, extend } from 'vee-validate'
import { FormMode } from '@/enum'
import BtnReset from '@/components/atoms/button/BtnReset.vue'
import PartCodeInput from '@/components/atoms/select/PartCodeInput.vue'
import CompanyInput from '@/components/atoms/select/CompanyInput.vue'
import TextFieldRef from '@/components/atoms/base/text-field/TextFieldRef.vue'
import ManufacturerInput from '@/components/atoms/select/ManufacturerInput.vue'
import SupplierPartInput from '@/components/atoms/select/SupplierPartInput.vue'
export default {
  name: 'EditReturnRecordForm',
  mixins: [APIUtils, Message, Loading, DateUtils],
  components: {
    TextFieldInput,
    Btn,
    BtnSearch,
    BtnNew,
    BtnSave,
    TextFieldInputWithDatePicker,
    TextAreaInput,
    BtnDelete,
    ConfirmDialog,
    ValidationObserver,
    ValidationProvider,
    BtnReset,
    PartCodeInput,
    CompanyInput,
    TextFieldRef,
    SupplierPartInput,
    ManufacturerInput,
  },
  props: {
    formMode: {
      type: String,
      required: false,
      default: null,
    },
    returnNo: {
      type: String,
      required: false,
      default: null,
    },
  },
  data() {
    return {
      settings: [
        {
          mode: FormMode.ADD,
          title: this.$t('ELE0016'),
          url: '/record/returnRecord/register',
        },
        {
          mode: FormMode.EDIT,
          title: this.$t('ELE0017'),
          url: '/record/returnRecord/{return_no}/update',
        },
        {
          mode: FormMode.EDITONLY,
          title: this.$t('ELE0017'),
          url: '/record/returnRecord/{return_no}/update',
        },
        {
          mode: FormMode.INQUIRY,
          title: this.$t('ELE0023'),
          url: '/record/returnRecord/{return_no}',
        },
        {
          mode: FormMode.NONE,
          title: '',
          url: '/record/returnRecord/{return_no}',
        },
      ],
      searchCondition: {
        returnNo: '',
      },
      inputItems: {
        returnNo: '',
        partCd: '',
        lotNo: '',
        returnCompanyCd: '',
        returnDate: '',
        returnQty: '',
        remarks: '',
      },
      acceptanceQty: '-',
      totalReturnedQty: '-',
      returnRate: '-',
      mode: FormMode.NONE,
      confirmDialog: {
        flag: false,
        confirmWord: '',
        btnLabel: '',
      },
    }
  },
  computed: {
    setting: {
      get() {
        return this.settings.find((el) => el.mode === this.mode)
      },
      set(value) {
        this.mode = value
      },
    },
    isAdd: {
      get() {
        return this.setting.mode === FormMode.ADD
      },
    },
    isEdit: {
      get() {
        return this.setting.mode === FormMode.EDIT
      },
    },
    isEditOnly: {
      get() {
        return this.setting.mode === FormMode.EDITONLY
      },
    },
    isInquiry: {
      get() {
        return this.setting.mode === FormMode.INQUIRY
      },
    },
    isNone: {
      get() {
        return this.setting.mode === FormMode.NONE
      },
    },
    isEpson: {
      get() {
        return this.$store.getters.isEpson
      },
    },
    getReturnRate: {
      get() {
        if (Number(this.acceptanceQty) > 0) {
          if (this.isInquiry) {
            return `${parseFloat(
              ((Number(this.totalReturnedQty) ? this.totalReturnedQty : 0) / Number(this.acceptanceQty)) * 100,
            ).toFixed(2)}%`
          }
          return `${parseFloat(
            ((Number(this.inputItems.returnQty ?? 0) + (Number(this.totalReturnedQty) ? this.totalReturnedQty : 0)) /
              Number(this.acceptanceQty)) *
              100,
          ).toFixed(2)}%`
        }
        return '-'
      },
    },
    getRemainingQty: {
      get() {
        return this.acceptanceQty - this.totalReturnedQty
      },
    },
    inputLabel: {
      get() {
        return this.setting.title
      },
    },
  },
  methods: {
    confirm() {
      if ((this.isEdit || this.isEditOnly) && this.inputItems.returnNo) {
        this.confirmDialog.confirmWord = this.$t('MSG0013')
        this.confirmDialog.btnLabel = this.$t('ELE0036')
        this.confirmDialog.flag = true
      } else {
        this.setErrorMessage(this.$t('MSG0019', { p0: this.$t('ELE0136') }))
      }
    },
    // async onReady() {
    //   console.log('onReady')
    //   if (this.inputItems.returnNo) {
    //     await this.$refs.observer.validate()
    //   }
    //   return ''
    // },
    resetAutoFillField() {
      this.acceptanceQty = '-'
      this.totalReturnedQty = '-'
      this.returnRate = '-'
    },
    async onPartSelectOrLotChange() {
      if (!this.inputItems.partCd || !this.inputItems.lotNo || !this.inputItems.returnCompanyCd) {
        this.resetAutoFillField()
        return
      }
      try {
        const body = {
          partCd: this.inputItems.partCd,
          lotNo: this.inputItems.lotNo,
          returnCompanyCd: this.inputItems.returnCompanyCd,
        }
        const response = await API.get('/box/getBoxes', body)
        if (response?.length > 0) {
          this.acceptanceQty = response.reduce((prev, current) => {
            return prev + current.qty
          }, 0)
          if (this.isAdd) {
            this.inputItems.returnCompanyCd = response[0].manufacturerCd
          }
        } else {
          this.resetAutoFillField()
        }
        const returnResponse = await API.get('/record/returnRecord', body)
        if (returnResponse?.length > 0) {
          const totalValue = 0
          this.totalReturnedQty =
            returnResponse
              .filter((item) => {
                if (this.isInquiry) {
                  return true
                }
                return item.returnNo !== this.inputItems.returnNo
              })
              ?.reduce((prev, current) => {
                return prev + current.returnQty
              }, totalValue) ?? '-'
        }
      } catch (error) {
        this.setErrorMessage(this.resolveErrorMessage(error, this.$t('MSG0002', { p0: this.$t('ELE0122') })))
      } finally {
        // if (revalidateFlag) await this.$refs.observer.validate()
      }
    },
    async onClickSearch() {
      if (!this.searchCondition.returnNo?.length) {
        return
      }
      this.startLoading()
      try {
        const currentURLMethod = this.settings.find((el) => el.mode == FormMode.NONE)
        const url = currentURLMethod?.url.replace('{return_no}', this.searchCondition.returnNo)
        const params = this.isEpson ? '' : { userCompanyCd: this.$store.getters.companyCd }
        const returnResponse = await API.get(url, params)
        if (returnResponse) {
          this.resetAutoFillField()
          this.resetInputField()
          this.inputItems = {
            ...this.inputItems,
            returnNo: returnResponse.returnNo,
            partCd: returnResponse.partCd,
            lotNo: returnResponse.lotNo,
            returnCompanyCd: returnResponse.returnCompanyCd,
            returnDate: returnResponse.returnDate,
            returnQty: returnResponse.returnQty,
            remarks: returnResponse.remarks,
            versionNo: returnResponse.versionNo,
          }
          if (this.isNone) {
            this.setting = FormMode.EDIT
          }
        }
      } catch (error) {
        this.resetField()
        this.setErrorMessage(this.resolveErrorMessage(error, this.$t('MSG0002', { p0: this.$t('ELE0122') })))
      } finally {
        this.stopLoading()
        this.onPartSelectOrLotChange()
      }
    },
    async onClickDelete() {
      try {
        this.startLoading()
        const url = `/record/returnRecord/${this.inputItems.returnNo}/delete`
        await API.post(url)
        this.setSuccessMessage(this.$t('MSG0014'))
        this.resetField()
      } catch (error) {
        this.setErrorMessage(this.resolveErrorMessage(error, this.$t('MSG0004', { p0: this.$t('ELE0122') })))
      } finally {
        this.stopLoading()
      }
    },
    async onClickSave() {
      try {
        this.startLoading()
        const url = this.setting.url
        let body = this.inputItems
        if (this.isAdd) {
          body = { ...body, returnNo: this.searchCondition.returnNo }
          const response = await API.post(url, body)
          if (response?.returnNo) {
            this.setSuccessMessage(this.$t('MSG0011', { p0: `${this.$t('ELE0122')} ${response.returnNo}` }))
            this.resetField()
            return
          }
        }
        if (this.isEdit || this.isEditOnly) {
          const updatedURL = url.replace('{return_no}', this.inputItems.returnNo)
          const editResponse = await API.post(updatedURL, body)
          if (editResponse == 'OK') {
            this.setSuccessMessage(this.$t('MSG0011', { p0: this.$t('ELE0122') }))
            this.resetField()
            return
          }
        }
      } catch (error) {
        this.setErrorMessage(this.resolveErrorMessage(error, this.$t('MSG0003', { p0: this.$t('ELE0122') })))
      } finally {
        this.stopLoading()
      }
    },
    onClickNew() {
      this.resetField()
      this.setting = FormMode.ADD
    },
    async onClickReset() {
      this.resetField()
    },
    onDateChanged() {},
    onCompanySelect() {},
    cancel() {
      this.confirmDialog.flag = false
    },
    proceed() {
      this.confirmDialog.flag = false
      this.onClickDelete()
    },
    resetSearchField() {
      this.searchCondition = {
        returnNo: '',
      }
    },
    resetInputField() {
      this.inputItems = {
        returnNo: '',
        partCd: '',
        lotNo: '',
        returnCompanyCd: '',
        returnDate: '',
        returnQty: '',
        remarks: '',
      }
    },
    resetField() {
      if (this.isEditOnly) {
        this.$emit('update')
        setTimeout(() => {
          this.returnToSearchRecord()
        }, 500)
        return
      }
      this.setting = FormMode.NONE
      this.resetSearchField()
      this.resetInputField()
      this.resetAutoFillField()
    },
    /**
     * Return to Search Return Record when operation is over
     * 保存、削除したらSearchReturnRecordに戻る
     */
    returnToSearchRecord() {
      this.$router.back()
    },
  },
  mounted() {
    extend('maxValue', {
      validate: (value) => {
        if (this.acceptanceQty == '-') {
          return true
        }
        const remainingQty = this.acceptanceQty - (Number(this.totalReturnedQty) ? this.totalReturnedQty : 0)
        if (value > remainingQty) {
          return this.$t('MSG0026', { p0: '{_field_}', p1: '0', p2: remainingQty })
        }
        return true
      },
    })
    const recordNoFromURL = this.$route.query?.record
    const canEdit = this.$route.query?.edit
    if (recordNoFromURL) {
      if (canEdit) {
        this.setting = FormMode.EDITONLY
      } else {
        this.setting = FormMode.INQUIRY
      }
      this.searchCondition.returnNo = recordNoFromURL
      this.onClickSearch()
    }
  },
}
</script>
